import { useQuery } from '@tanstack/react-query';
import { type AiCapabilities, type ManualAlternative } from '@apm/contracts';
import { aiApi, aiKeys } from './api';

/**
 * "AI có dùng được không, và nếu không thì làm tay ở đâu" — ADR-006.
 *
 * Hỏi một lần rồi giữ lâu: trạng thái này chỉ đổi khi admin nhập key hoặc hạn
 * mức chạm trần, không phải mỗi lần chuyển màn hình.
 */
/**
 * `projectId` có thì câu trả lời tính cả **key của dự án** —
 * docs/plan-project-ai-key.md.
 *
 * Bỏ trống ở màn hình cá nhân và trang tổng quan. Truyền vào ở màn hình của một
 * dự án, nếu không thành viên chưa nhập key riêng sẽ thấy nút AI tắt trong một
 * dự án đã gắn key — trong khi lời gọi thật vẫn chạy.
 *
 * Khoá truy vấn mang theo `projectId`, nên hai dự án không dùng chung một câu
 * trả lời đã cache.
 */
export function useAiCapabilities(projectId?: string) {
  return useQuery({
    queryKey: aiKeys.capabilities(projectId),
    queryFn: () => aiApi.capabilities(projectId),
    staleTime: 5 * 60_000,
    // Không retry: endpoint này luôn 200 khi hệ thống chạy được. Hỏng ở đây là
    // hỏng thật, và thử lại chỉ làm màn hình đứng lâu hơn trước khi hiện đường
    // làm thủ công.
    retry: false,
  });
}

export interface SkillAvailability {
  readonly available: boolean;
  /** Luôn có, kể cả khi `available` là `true` — người dùng vẫn được chọn làm tay. */
  readonly manual: ManualAlternative | null;
  /** Câu giải thích khi tắt. `null` khi đang bật. */
  readonly message: string | null;
}

/**
 * Một skill cụ thể có dùng được không.
 *
 * Trả `available: false` cả khi query đang chạy hoặc lỗi — mặc định **an toàn**
 * là hiện đường làm tay. Mặc định ngược lại sẽ nhấp nháy một nút AI rồi tắt đi,
 * hoặc tệ hơn, cho bấm vào một nút chắc chắn lỗi.
 */
export function skillAvailability(
  name: string,
  capabilities: AiCapabilities | undefined,
): SkillAvailability {
  const manual = capabilities?.manualFallbacks.find((item) => item.name === name)?.manual ?? null;

  if (!capabilities?.enabled) {
    return { available: false, manual, message: capabilities?.message ?? null };
  }

  const skill = capabilities.skills.find((item) => item.name === name);
  // Skill vắng mặt khi AI đang bật nghĩa là **thiếu quyền**, không phải thiếu
  // key — nên không mượn câu `capabilities.message` (nó nói về key/hạn mức).
  if (!skill) return { available: false, manual, message: null };

  return { available: true, manual, message: null };
}
